/**
 * Map3DWithStreetViewControl - 3D Map with Street View Pegman overlay
 * RoofMapper v3 - NIKO Energy
 */

import React, { useEffect, useRef, useState } from 'react';
import { useMap } from '../../contexts/MapContext';
import Map3DViewFinal from './Map3DViewFinal';

interface Map3DWithStreetViewControlProps {
  drawingMode?: 'none' | 'measure' | 'polygon' | 'panel';
}

export default function Map3DWithStreetViewControl({ 
  drawingMode = 'none' 
}: Map3DWithStreetViewControlProps): JSX.Element {
  const { pinPosition, cameraState, setMapMode } = useMap();
  const controlMapRef = useRef<google.maps.Map | null>(null);
  const controlContainerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);

  // Initialize invisible 2D map for Street View control
  useEffect(() => {
    const initControlMap = async () => {
      try {
        if (!window.google?.maps) {
          console.error('Google Maps API not loaded');
          return;
        }

        if (!controlContainerRef.current) {
          console.error('Control container ref not ready');
          return;
        }

        const position = pinPosition || cameraState.center || {
          lat: 19.42241280654638,
          lng: -99.21513116670044,
        };

        // Create an invisible 2D map just for the Street View control (Pegman)
        const map = new google.maps.Map(controlContainerRef.current, {
          center: { lat: position.lat, lng: position.lng },
          zoom: 19,
          disableDefaultUI: true, // Hide all controls
          streetViewControl: true, // Enable only Street View (Pegman)
          streetViewControlOptions: {
            position: google.maps.ControlPosition.RIGHT_BOTTOM,
          },
          backgroundColor: 'transparent',
        });

        controlMapRef.current = map;

        // Listen for Street View visibility changes
        const streetView = map.getStreetView();
        streetView.addListener('visible_changed', () => {
          const isVisible = streetView.getVisible();
          
          if (isVisible) {
            // User opened Street View via Pegman - switch to STREET mode
            setMapMode('STREET');
          }
        });

        setIsReady(true);
      } catch (error) {
        console.error('Error initializing Street View control:', error);
      }
    };

    const timeout = setTimeout(initControlMap, 500);
    return () => clearTimeout(timeout);
  }, []);

  // Update control map position when pin changes
  useEffect(() => {
    if (!controlMapRef.current || !pinPosition) return;

    controlMapRef.current.setCenter({ lat: pinPosition.lat, lng: pinPosition.lng });
  }, [pinPosition]);

  return (
    <div className="relative w-full h-full">
      {/* 3D Map (main view) */}
      <Map3DViewFinal drawingMode={drawingMode} />

      {/* Invisible 2D map overlay ONLY for Street View Pegman control */}
      <div 
        ref={controlContainerRef}
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.01, // Nearly invisible but must exist for control to work
          zIndex: 100, // Above 3D map
        }}
      />

      {/* Re-enable pointer events only on the Street View control area */}
      <style>{`
        .gm-svpc,
        .gm-style-mtc,
        .gmnoprint {
          pointer-events: auto !important;
        }
      `}</style>
    </div>
  );
}

